import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../src/lib/supabase';
import { Fatura } from '../../types';
import { Loader2, Zap, TrendingUp, BarChart2, Battery, Calendar } from 'lucide-react';
import { ucCodigoKeys } from '../../src/lib/ucCodigo';
import { 
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, 
    Legend, ResponsiveContainer, Line, ComposedChart 
} from 'recharts';

interface ConsumoGeracaoTabProps {
    unidadeId: string;
    numeroUc: string;
    outroNumeroUc?: string | null;
}

const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const ConsumoGeracaoTab: React.FC<ConsumoGeracaoTabProps> = ({ unidadeId, numeroUc, outroNumeroUc }) => {
    const [loading, setLoading] = useState(true);
    const [faturas, setFaturas] = useState<Fatura[]>([]);
    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

    useEffect(() => {
        const fetchDados = async () => {
            setLoading(true);
            try {
                const { data, error } = await supabase
                    .from('faturas')
                    .select('*')
                    .in('uc_codigo_norm', ucCodigoKeys(numeroUc, outroNumeroUc))
                    .neq('status_fatura', 'Excluído');

                if (error) throw error;
                setFaturas(data || []);
                
                // Set default selected year to the most recent fatura year, if any.
                if (data && data.length > 0) {
                    const yearsStr = data.map(f => {
                        const parts = f.mes_referencia?.split('/');
                        return parts && parts.length === 2 ? parseInt(parts[1], 10) : 0;
                    }).filter(y => y > 0);
                    
                    if (yearsStr.length > 0) {
                        setSelectedYear(Math.max(...yearsStr));
                    }
                }
                
            } catch (err) {
                console.error("Erro ao buscar faturas para consumo:", err);
            } finally {
                setLoading(false);
            }
        };

        if (numeroUc) {
            fetchDados();
        }
    }, [numeroUc, outroNumeroUc]);

    const anosDisponiveis = useMemo(() => {
        const years = new Set<number>();
        faturas.forEach(f => {
            const parts = f.mes_referencia?.split('/');
            if (parts && parts.length === 2) {
                const year = parseInt(parts[1], 10);
                if (!isNaN(year)) years.add(year);
            }
        });
        
        const currentYear = new Date().getFullYear();
        years.add(currentYear); // Always include current year even if no data yet
        
        return Array.from(years).sort((a, b) => b - a);
    }, [faturas]);

    interface ChartDataRow {
        name: string;
        ConsumoAnterior?: number;
        ConsumoReal?: number;
        ConsumoPrevisto?: number;
        InjecaoReal?: number;
        InjecaoPrevista?: number;
        hasDataAtual?: boolean;
    }

    // Data Processing logic
    const { chartData, kpis } = useMemo<{ chartData: ChartDataRow[], kpis: any }>(() => {
        if (!faturas || faturas.length === 0) {
            return { 
                chartData: MONTHS.map(m => ({ name: m })), 
                kpis: { consumoTotal: 0, injecaoTotal: 0, taxaCobertura: 0, saldoPrevisto: 0 } 
            };
        }

        // 1. Calculate overall historical monthly averages for the given UC (for "Previsão")
        let sumConsumo = 0;
        let sumInjecao = 0;
        let countHist = 0;

        faturas.forEach(f => {
            const c = f.consumo_ativo_kwh || 0;
            const i = (f.energia_injetada_gd1_kwh || 0) + (f.energia_injetada_gd2_kwh || 0);
            sumConsumo += c;
            sumInjecao += i;
            countHist++;
        });

        const avgConsumo = countHist > 0 ? Math.round(sumConsumo / countHist) : 0;
        const avgInjecao = countHist > 0 ? Math.round(sumInjecao / countHist) : 0;

        // 2. Build the dataset for the selected year and the previous year
        let anoAtualConsumoReal = 0;
        let anoAtualConsumoPrevisto = 0;
        let anoAtualInjecaoReal = 0;
        let anoAtualInjecaoPrevista = 0;

        const dataArr = MONTHS.map((monthName, index) => {
            const monthStr = (index + 1).toString().padStart(2, '0');
            const targetRef = `${monthStr}/${selectedYear}`;
            const previousRef = `${monthStr}/${selectedYear - 1}`;

            // Find invoices for target and previous year
            const faturaAtual = faturas.find(f => f.mes_referencia === targetRef);
            const faturaAnterior = faturas.find(f => f.mes_referencia === previousRef);

            const consumoAnterior = faturaAnterior ? (faturaAnterior.consumo_ativo_kwh || 0) : 0;
            
            let consumoReal = null;
            let consumoPrevisto = null;
            let injecaoReal = null;
            let injecaoPrevista = null;

            if (faturaAtual) {
                consumoReal = faturaAtual.consumo_ativo_kwh || 0;
                injecaoReal = (faturaAtual.energia_injetada_gd1_kwh || 0) + (faturaAtual.energia_injetada_gd2_kwh || 0);
                
                anoAtualConsumoReal += consumoReal;
                anoAtualInjecaoReal += injecaoReal;
            } else {
                // Previsão para os meses sem lançamento
                consumoPrevisto = avgConsumo;
                injecaoPrevista = avgInjecao;

                anoAtualConsumoPrevisto += consumoPrevisto;
                anoAtualInjecaoPrevista += injecaoPrevista;
            }

            return {
                name: monthName,
                ConsumoAnterior: consumoAnterior > 0 ? consumoAnterior : undefined,
                ConsumoReal: consumoReal !== null ? consumoReal : undefined,
                ConsumoPrevisto: consumoPrevisto !== null ? consumoPrevisto : undefined,
                InjecaoReal: injecaoReal !== null ? injecaoReal : undefined,
                InjecaoPrevista: injecaoPrevista !== null ? injecaoPrevista : undefined,
                
                hasDataAtual: faturaAtual !== undefined
            };
        });

        // Calculate KPIs (Year projection = Real so far + Forecast for remaining)
        const consumoTotal = anoAtualConsumoReal + anoAtualConsumoPrevisto;
        const injecaoTotal = anoAtualInjecaoReal + anoAtualInjecaoPrevista;
        
        const saldoPrevisto = injecaoTotal - consumoTotal;
        const taxaCobertura = consumoTotal > 0 ? (injecaoTotal / consumoTotal) * 100 : 0;

        return {
            chartData: dataArr,
            kpis: {
                consumoTotal,
                injecaoTotal,
                taxaCobertura,
                saldoPrevisto
            }
        };

    }, [faturas, selectedYear]);

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center p-12 text-gray-400">
                <Loader2 className="animate-spin mb-2" size={32} />
                <p>Analisando histórico de consumo e injeção...</p>
            </div>
        );
    }

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-3 rounded-lg shadow-xl text-sm">
                    <p className="font-bold text-gray-900 border-b border-gray-100 dark:border-gray-700 pb-2 mb-2">{label}</p>
                    {payload.map((entry: any, index: number) => (
                        <div key={index} className="flex justify-between items-center gap-4 py-1">
                            <span className="flex items-center gap-2">
                                <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: entry.color }}></span>
                                <span className="text-gray-600 dark:text-gray-300">{entry.name}</span>
                            </span>
                            <span className="font-bold text-gray-900 dark:text-white">{entry.value} kWh</span>
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };

    return (
        <div className="p-6 space-y-6 animate-fade-in">

            {/* Header controls */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4 bg-gray-50/50 dark:bg-gray-800/20 p-4 rounded-xl border border-gray-100 dark:border-gray-800">
                <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <BarChart2 className="text-primary" size={20} />
                        Análise de Performance Energética
                    </h3>
                    <p className="text-sm text-gray-500">Histórico real combinado com simulações de cenários baseados na média da UC.</p>
                </div>

                <div className="flex flex-col space-y-1">
                    <label className="text-[10px] uppercase font-bold text-gray-500">Ano de Avaliação</label>
                    <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                        <select
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(Number(e.target.value))}
                            className="pl-9 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white font-medium focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none cursor-pointer appearance-none min-w-[120px]"
                        >
                            {anosDisponiveis.map(ano => (
                                <option key={ano} value={ano}>{ano}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm flex flex-col justify-center">
                    <div className="flex items-center gap-2 mb-2">
                        <Zap size={16} className="text-red-500" />
                        <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Demanda Local (Ano)</p>
                    </div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{kpis.consumoTotal} <span className="text-sm text-gray-500 font-normal">kWh</span></p>
                    <p className="text-xs text-gray-400 mt-1 mt-1">Real + Previsão projetada</p>
                </div>
                
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm flex flex-col justify-center">
                    <div className="flex items-center gap-2 mb-2">
                        <TrendingUp size={16} className="text-emerald-500" />
                        <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Injeção Recebida</p>
                    </div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{kpis.injecaoTotal} <span className="text-sm text-gray-500 font-normal">kWh</span></p>
                    <p className="text-xs text-gray-400 mt-1">Real + Previsão projetada</p>
                </div>

                <div className={`rounded-xl border p-4 shadow-sm flex flex-col justify-center ${kpis.saldoPrevisto >= 0 ? 'bg-emerald-50/50 border-emerald-200 dark:bg-emerald-900/10 dark:border-emerald-800/50' : 'bg-red-50/50 border-red-200 dark:bg-red-900/10 dark:border-red-800/50'}`}>
                    <div className="flex items-center gap-2 mb-2">
                        <Battery size={16} className={kpis.saldoPrevisto >= 0 ? "text-emerald-600" : "text-red-600"} />
                        <p className={`text-[11px] font-bold uppercase tracking-wider ${kpis.saldoPrevisto >= 0 ? "text-emerald-700 dark:text-emerald-400" : "text-red-700 dark:text-red-400"}`}>Saldo Final do Ano</p>
                    </div>
                    <p className={`text-2xl font-bold ${kpis.saldoPrevisto >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
                        {kpis.saldoPrevisto > 0 ? '+' : ''}{kpis.saldoPrevisto} <span className="text-sm font-normal">kWh</span>
                    </p>
                    <p className={`text-xs mt-1 ${kpis.saldoPrevisto >= 0 ? "text-emerald-600/70" : "text-red-600/70"}`}>
                        Estimativa de excedente/déficit
                    </p>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm flex flex-col justify-center">
                    <div className="flex items-center gap-2 mb-2">
                        <BarChart2 size={16} className="text-blue-500" />
                        <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Tx. de Cobertura</p>
                    </div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{kpis.taxaCobertura.toFixed(1)}%</p>
                    <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-1.5 mt-2">
                        <div 
                            className="bg-blue-500 h-1.5 rounded-full" 
                            style={{ width: `${Math.min(kpis.taxaCobertura, 100)}%` }}
                        ></div>
                    </div>
                </div>
            </div>

            {/* Main Chart */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm relative">
                <h4 className="text-center font-bold text-gray-500 uppercase text-sm tracking-wider mb-6">Emissões e Injeção Histórica / Projetada ({selectedYear})</h4>
                <div className="h-[400px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" className="dark:opacity-10" />
                            <XAxis 
                                dataKey="name" 
                                axisLine={false} 
                                tickLine={false} 
                                tick={{ fontSize: 12, fill: '#6B7280' }} 
                                dy={10}
                            />
                            <YAxis 
                                axisLine={false} 
                                tickLine={false} 
                                tick={{ fontSize: 12, fill: '#6B7280' }}
                            />
                            <RechartsTooltip content={<CustomTooltip />} />
                            
                            <Legend wrapperStyle={{ paddingTop: '20px' }} />

                            <Bar 
                                dataKey="ConsumoAnterior" 
                                name={`Consumo (${selectedYear - 1})`} 
                                fill="#10B981" 
                                radius={[4, 4, 0, 0]} 
                                barSize={20}
                            />
                            <Bar 
                                dataKey="ConsumoReal" 
                                name={`Consumo Real (${selectedYear})`} 
                                fill="#3B82F6" 
                                radius={[4, 4, 0, 0]} 
                                barSize={20}
                            />
                            <Bar 
                                dataKey="ConsumoPrevisto" 
                                name={`Consumo Previsto (${selectedYear})`} 
                                fill="#BFDBFE" 
                                radius={[4, 4, 0, 0]} 
                                barSize={20}
                            />
                            
                            <Line 
                                type="monotone" 
                                dataKey="InjecaoReal" 
                                name={`Injeção Real`} 
                                stroke="#F59E0B" 
                                strokeWidth={3}
                                dot={{ r: 4, strokeWidth: 2, fill: '#fff' }}
                                activeDot={{ r: 6 }}
                                connectNulls
                            />
                            <Line 
                                type="monotone" 
                                dataKey="InjecaoPrevista" 
                                name={`Injeção Prevista`} 
                                stroke="#FCD34D" 
                                strokeWidth={3}
                                strokeDasharray="5 5"
                                dot={{ r: 4, strokeWidth: 2, fill: '#fff' }}
                                connectNulls
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Config & Table row */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
                <div className="p-4 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                    <h4 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        Detalhamento Mês a Mês ({selectedYear})
                    </h4>
                </div>
                <div className="overflow-x-auto scroller">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50/50 dark:bg-gray-800/50 text-gray-500 uppercase text-[10px] font-bold tracking-wider border-b border-gray-200 dark:border-gray-700">
                            <tr>
                                <th className="px-4 py-3">Mês</th>
                                <th className="px-4 py-3 text-right">Consumo {selectedYear - 1}</th>
                                <th className="px-4 py-3 text-right">Consumo {selectedYear}</th>
                                <th className="px-4 py-3 text-right">Injeção {selectedYear}</th>
                                <th className="px-4 py-3 text-right">Saldo Período</th>
                                <th className="px-4 py-3 text-center">Origem</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                            {chartData.map((row, idx) => {
                                const cons = row.ConsumoReal ?? row.ConsumoPrevisto ?? 0;
                                const inj = row.InjecaoReal ?? row.InjecaoPrevista ?? 0;
                                const saldo = inj - cons;

                                return (
                                    <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                                        <td className="px-4 py-3 font-semibold text-gray-900 dark:text-white">{row.name}</td>
                                        <td className="px-4 py-3 text-right text-gray-500">{row.ConsumoAnterior || '-'}</td>
                                        <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-white">{cons}</td>
                                        <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-white">{inj}</td>
                                        <td className={`px-4 py-3 text-right font-bold ${saldo >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                            {saldo > 0 ? '+' : ''}{saldo}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold border ${row.hasDataAtual 
                                                ? 'bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-900/30 dark:border-blue-800/50 dark:text-blue-400' 
                                                : 'bg-yellow-50 text-yellow-600 border-yellow-200 dark:bg-yellow-900/30 dark:border-yellow-800/50 dark:text-yellow-400'}`}>
                                                {row.hasDataAtual ? 'Real' : 'Projeção Média'}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

        </div>
    );
};

export default ConsumoGeracaoTab;
